import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAuth } from '@/auth/useAuth';
import { queryKeys } from '@/config/query-keys';
import { getApiErrorMessage } from '@/lib/error-codes';
import { PaymentsApi } from '@/lib/payments-api';
import type { CreatePaymentRequest } from '@/types/payment';

const STALE_TIME = 1000 * 60 * 5;

export function usePaymentOptions(enabled = true) {
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId;

  return useQuery({
    queryKey: queryKeys.payments.options(customerId ?? 0),
    queryFn: () => PaymentsApi.getPaymentOptions(),
    staleTime: STALE_TIME,
    enabled: enabled && customerId != null,
  });
}

export function usePayment(paymentId: string | null, pollWhilePending = false) {
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId;

  return useQuery({
    queryKey: queryKeys.payments.detail(customerId ?? 0, paymentId ?? ''),
    queryFn: () => PaymentsApi.getPayment(paymentId!),
    enabled: !!paymentId && customerId != null,
    refetchInterval: (query) => {
      if (!pollWhilePending) {
        return false;
      }

      const status = query.state.data?.status;
      return status === 'pending' ? 2000 : false;
    },
  });
}

export function useVaultMethods() {
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId;

  return useQuery({
    queryKey: queryKeys.payments.vault(customerId ?? 0),
    queryFn: () => PaymentsApi.getVaultMethods(),
    staleTime: STALE_TIME,
    enabled: customerId != null,
  });
}

export function usePaymentMutations() {
  const queryClient = useQueryClient();
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId;

  const invalidatePaymentData = () => {
    if (customerId == null) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.payments.all(customerId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all(customerId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all(customerId) });
  };

  const createPayment = useMutation({
    mutationFn: (data: CreatePaymentRequest) => PaymentsApi.createPayment(data),
    onSuccess: () => {
      invalidatePaymentData();
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error);
      toast.error(message);
    },
  });

  // Returned from onSuccess so the mutation stays pending until the list is
  // refetched: otherwise a row shows its old state for the length of the refetch.
  const refetchMethods = () =>
    customerId == null
      ? Promise.resolve()
      : queryClient.invalidateQueries({ queryKey: queryKeys.payments.all(customerId) });

  const deleteVaultMethod = useMutation({
    mutationFn: (methodId: string) => PaymentsApi.deleteVaultMethod(methodId),
    onSuccess: refetchMethods,
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error));
      // A method removed elsewhere answers 404; the refetch drops its stale row.
      void refetchMethods();
    },
  });

  const setDefaultVaultMethod = useMutation({
    mutationFn: (methodId: string) => PaymentsApi.setDefaultVaultMethod(methodId),
    onSuccess: refetchMethods,
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error));
      // A method removed elsewhere answers 404; the refetch drops its stale row.
      void refetchMethods();
    },
  });

  return { createPayment, deleteVaultMethod, setDefaultVaultMethod, invalidatePaymentData };
}
